/**
 * tus 1.0 istemcisi (mimari belge §6.6, §10.8; ADR 0007; plan M8a Task 17): dosya parça parça
 * yüklenir; bağlantı koparsa aynı dosya yeniden seçilince sunucunun bildirdiği yerden devam eder.
 * Yüklemenin adresi tarayıcıda dosyanın parmak iziyle saklanır (ad, boyut, değişiklik zamanı,
 * amaç). Ek bağımlılık yok; yükleme silinmez (sunucu DELETE'i reddeder).
 */
export const UPLOADS_URL = '/api/office/uploads';
/** Sunucunun S3 parça boyutu (8 MiB); parçalar onunla hizalı. */
export const TUS_CHUNK_BYTES = 8 * 1024 * 1024;
const TUS = { 'tus-resumable': '1.0.0' };

export interface TusStorage {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

export interface TusOptions {
  /** Yüklemenin amacı (`document_revision`): sunucu sınırı buna göre koyar (ADR 0028 EK). */
  purpose: string;
  /**
   * Oturumdaki kişi: devam kaydı kişiye bağlıdır; paylaşılan tarayıcıda başka kişinin yarım
   * yüklemesi devralınmaz (inceleme M8a3-R22). Sunucu da yalnız yüklemeyi açana izin verir.
   */
  owner?: string;
  onProgress: (progress: { sent: number; total: number }) => void;
  fetch?: typeof fetch;
  /** Kaldığı yerden devam için adres deposu; `null`: saklanmaz. Varsayılan `localStorage`. */
  storage?: TusStorage | null;
  chunkSize?: number;
  signal?: AbortSignal;
}

/** Yükleme hatası: `NETWORK` (yeniden denenince sürer), `FILE_TOO_LARGE`, `UPLOAD_FAILED`. */
export class UploadError extends Error {
  readonly code: string;
  constructor(code: string) {
    super(code);
    this.code = code;
  }
}

type UploadFile = Blob & { name: string; lastModified: number };

const FINGERPRINT_PREFIX = 'felix-tus:';

export function uploadFingerprint(file: UploadFile, purpose: string, owner = ''): string {
  return `${FINGERPRINT_PREFIX}${owner}:${purpose}:${file.name}:${file.size}:${file.lastModified}`;
}

/**
 * Çıkışta tarayıcıdaki bütün devam kayıtları silinir (inceleme M8a3-R22): yarım yüklemelerin
 * adresi ve dosya adları sonraki kişiye kalmaz.
 */
export function clearUploadResumes(
  storage:
    | Pick<Storage, 'length' | 'key' | 'removeItem'>
    | null
    | undefined = browserLocalStorage(),
): void {
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key?.startsWith(FINGERPRINT_PREFIX)) keys.push(key);
    }
    for (const key of keys) storage.removeItem(key);
  } catch {
    // Gizli pencere ya da engelli depo: silinecek kayıt da yok
  }
}

function browserLocalStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** tus üst verisi: "anahtar base64" çiftleri; değer UTF-8. */
function encode(value: string): string {
  let binary = '';
  for (const byte of new TextEncoder().encode(value)) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function browserStorage(): TusStorage | null {
  try {
    const storage = globalThis.localStorage;
    if (!storage) return null;
    return {
      get: (key) => {
        try {
          return storage.getItem(key);
        } catch {
          return null;
        }
      },
      set: (key, value) => {
        try {
          storage.setItem(key, value);
        } catch {
          // Gizli pencere ya da dolu depo: yükleme yine çalışır, yalnız devam edemez
        }
      },
      remove: (key) => {
        try {
          storage.removeItem(key);
        } catch {
          // yok sayılır
        }
      },
    };
  } catch {
    return null;
  }
}

function failure(status: number): UploadError {
  return new UploadError(status === 413 ? 'FILE_TOO_LARGE' : 'UPLOAD_FAILED');
}

/** Dosyayı yükler; yükleme kimliğini (adresin UUID'si) döner. */
export async function tusUpload(file: UploadFile, options: TusOptions): Promise<string> {
  const fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
  const storage = options.storage === undefined ? browserStorage() : options.storage;
  const chunk = options.chunkSize ?? TUS_CHUNK_BYTES;
  const key = uploadFingerprint(file, options.purpose, options.owner);
  const total = file.size;

  const send = async (url: string, init: RequestInit): Promise<Response> => {
    try {
      return await fetcher(url, {
        credentials: 'same-origin',
        ...(options.signal ? { signal: options.signal } : {}),
        ...init,
      });
    } catch {
      if (options.signal?.aborted) throw new UploadError('CANCELLED');
      throw new UploadError('NETWORK');
    }
  };

  /** Sunucunun bildiği yer; yükleme yoksa ya da başkasınınsa `null`. */
  const resume = async (url: string): Promise<number | null> => {
    const response = await send(url, { method: 'HEAD', headers: TUS });
    if (!response.ok) return null;
    const offset = Number(response.headers.get('upload-offset'));
    const length = Number(response.headers.get('upload-length'));
    return Number.isInteger(offset) && (Number.isNaN(length) || length === total) ? offset : null;
  };

  let url = storage?.get(key) ?? null;
  let offset: number | null = url ? await resume(url) : null;
  if (url === null || offset === null) {
    const response = await send(UPLOADS_URL, {
      method: 'POST',
      headers: {
        ...TUS,
        'upload-length': String(total),
        'upload-metadata': `filename ${encode(file.name)},purpose ${encode(options.purpose)}`,
      },
    });
    const location = response.headers.get('location');
    if (response.status !== 201 || !location) throw failure(response.status);
    url = location.startsWith('/') ? location : `${UPLOADS_URL}/${location.split('/').at(-1)}`;
    offset = 0;
    storage?.set(key, url);
  }
  options.onProgress({ sent: offset, total });

  let resynced = false;
  while (offset < total) {
    const body = file.slice(offset, Math.min(total, offset + chunk));
    const response = await send(url, {
      method: 'PATCH',
      headers: {
        ...TUS,
        'upload-offset': String(offset),
        'content-type': 'application/offset+octet-stream',
      },
      body,
    });
    if (response.status === 409 && !resynced) {
      // Yer kaydı: sunucunun bildiği yerden bir kez daha
      resynced = true;
      const known = await resume(url);
      if (known === null) throw failure(409);
      offset = known;
      continue;
    }
    if (response.status !== 204) {
      if (response.status === 404 || response.status === 410) storage?.remove(key);
      throw failure(response.status);
    }
    const next = Number(response.headers.get('upload-offset'));
    if (!Number.isInteger(next) || next <= offset) throw failure(response.status);
    offset = next;
    resynced = false;
    options.onProgress({ sent: offset, total });
  }
  storage?.remove(key);
  return url.split('/').at(-1) ?? '';
}
